import { KeyRound, Plus, RotateCcw, Trash2, UserX } from "lucide-react";
import { FormEvent, useEffect, useState } from "react";
import { ApiError, api } from "../lib/api";
import { canManageUsers, useAuth } from "../lib/auth";
import type { Role, User } from "../lib/types";

const roles: Role[] = ["owner", "admin", "editor", "viewer"];

const emptyForm = {
  username: "",
  displayName: "",
  password: "",
  role: "editor" as Role
};

export function UsersPage() {
  const { user } = useAuth();
  const [items, setItems] = useState<User[]>([]);
  const [form, setForm] = useState(emptyForm);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  const load = async () => {
    try {
      const result = await api.users();
      setItems(result.items);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "โหลด users ไม่สำเร็จ");
    }
  };

  useEffect(() => {
    load();
  }, []);

  if (!canManageUsers(user)) {
    return <div className="rounded border border-line bg-white p-6 text-sm text-slate-600">role นี้ไม่มีสิทธิ์จัดการ Users</div>;
  }

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setError("");
    setNotice("");
    try {
      await api.createUser(form);
      setForm(emptyForm);
      setNotice("เพิ่ม user แล้ว");
      load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "เพิ่ม user ไม่สำเร็จ");
    }
  };

  const updateRole = async (target: User, role: Role) => {
    await api.updateUser(target.id, { role });
    load();
  };

  const updateActive = async (target: User, active: boolean) => {
    await api.updateUser(target.id, { active });
    load();
  };

  const resetPassword = async (target: User) => {
    const password = window.prompt(`รหัสผ่านใหม่สำหรับ ${target.username}`);
    if (!password) return;
    try {
      await api.resetPassword(target.id, password);
      setNotice("รีเซ็ตรหัสผ่านแล้ว");
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "รีเซ็ตรหัสผ่านไม่สำเร็จ");
    }
  };

  return (
    <div className="grid gap-6 xl:grid-cols-[380px_1fr]">
      <form onSubmit={submit} className="h-fit rounded border border-line bg-white p-4 shadow-sm">
        <h1 className="text-xl font-semibold text-ink">Add User</h1>
        <p className="mt-1 text-sm text-slate-500">สร้างบัญชีทีมงานด้วย username/password และ role</p>

        {error ? <div className="mt-4 rounded border border-red-200 bg-red-50 p-3 text-sm text-red-700">{error}</div> : null}
        {notice ? <div className="mt-4 rounded border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-700">{notice}</div> : null}

        <label className="mt-4 block">
          <span className="mb-1 block text-sm font-medium text-slate-700">Username</span>
          <input
            value={form.username}
            onChange={(event) => setForm({ ...form, username: event.target.value })}
            className="focus-ring w-full rounded border border-line px-3 py-2"
            minLength={3}
            required
          />
        </label>

        <label className="mt-4 block">
          <span className="mb-1 block text-sm font-medium text-slate-700">Display name</span>
          <input
            value={form.displayName}
            onChange={(event) => setForm({ ...form, displayName: event.target.value })}
            className="focus-ring w-full rounded border border-line px-3 py-2"
            required
          />
        </label>

        <label className="mt-4 block">
          <span className="mb-1 block text-sm font-medium text-slate-700">Password</span>
          <input
            type="password"
            value={form.password}
            onChange={(event) => setForm({ ...form, password: event.target.value })}
            className="focus-ring w-full rounded border border-line px-3 py-2"
            minLength={10}
            required
          />
        </label>

        <label className="mt-4 block">
          <span className="mb-1 block text-sm font-medium text-slate-700">Role</span>
          <select
            value={form.role}
            onChange={(event) => setForm({ ...form, role: event.target.value as Role })}
            className="focus-ring w-full rounded border border-line bg-white px-3 py-2"
          >
            {roles.map((role) => (
              <option key={role} value={role}>
                {role}
              </option>
            ))}
          </select>
        </label>

        <button type="submit" className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded bg-brand px-4 py-2 text-sm font-semibold text-white hover:bg-teal-800">
          <Plus size={16} />
          Add User
        </button>
      </form>

      <section className="overflow-hidden rounded border border-line bg-white shadow-sm">
        <table className="w-full min-w-[860px] text-left text-sm">
          <thead className="bg-slate-50 text-xs uppercase text-slate-500">
            <tr>
              <th className="px-4 py-3">User</th>
              <th className="px-4 py-3">Role</th>
              <th className="px-4 py-3">Active</th>
              <th className="px-4 py-3">Last Login</th>
              <th className="px-4 py-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item) => (
              <tr key={item.id} className="border-t border-line">
                <td className="px-4 py-3">
                  <div className="font-medium text-ink">{item.displayName}</div>
                  <div className="text-xs text-slate-500">@{item.username}</div>
                </td>
                <td className="px-4 py-3">
                  <select
                    value={item.role}
                    onChange={(event) => updateRole(item, event.target.value as Role)}
                    className="focus-ring rounded border border-line bg-white px-2 py-1"
                    disabled={item.id === user?.id && item.role === "owner"}
                  >
                    {roles.map((role) => (
                      <option key={role} value={role}>
                        {role}
                      </option>
                    ))}
                  </select>
                </td>
                <td className="px-4 py-3">
                  <label className="inline-flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={item.active}
                      onChange={(event) => updateActive(item, event.target.checked)}
                      disabled={item.id === user?.id}
                    />
                    {item.active ? "Active" : "Disabled"}
                  </label>
                </td>
                <td className="px-4 py-3">{item.lastLoginAt ? new Date(item.lastLoginAt).toLocaleString("th-TH") : "-"}</td>
                <td className="px-4 py-3">
                  <div className="flex gap-2">
                    <button onClick={() => resetPassword(item)} className="rounded border border-line p-2 text-slate-600 hover:bg-slate-100" title="Reset password">
                      <KeyRound size={16} />
                    </button>
                    <button onClick={() => api.disableUser(item.id).then(load)} className="rounded border border-line p-2 text-slate-600 hover:bg-slate-100" title="Disable">
                      <UserX size={16} />
                    </button>
                    <button
                      onClick={() => api.deleteUser(item.id).then(load)}
                      className="rounded border border-red-200 p-2 text-red-600 hover:bg-red-50"
                      title="Delete"
                      disabled={item.id === user?.id}
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {items.length === 0 ? <div className="p-6 text-sm text-slate-500">ยังไม่มี user</div> : null}
      </section>
    </div>
  );
}
